/**
 * Notification system: toast popups and comms log panel.
 * Manages the comms log state and toast display.
 */

// =============================================================================
// TYPES
// =============================================================================

export interface CommsNotification {
  type: 'gcppa' | 'broadcast';
  message: string;
  sender: string;
  color: string;
}

// =============================================================================
// STATE
// =============================================================================

let commsLogOpen = false;
let toastsEnabled = true;
const commsHistory: CommsNotification[] = [];

// =============================================================================
// TOAST POPUPS
// =============================================================================

function showToast(notif: CommsNotification): void {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const toast = document.createElement('div');
  toast.className = 'toast toast-enter';

  const senderSpan = document.createElement('span');
  senderSpan.className = 'toast-sender';
  senderSpan.style.color = notif.color;
  senderSpan.textContent = notif.sender;

  const msgSpan = document.createElement('span');
  msgSpan.className = 'toast-message';
  msgSpan.textContent = notif.message;

  toast.appendChild(senderSpan);
  toast.appendChild(msgSpan);
  container.appendChild(toast);

  // Auto-dismiss after 4s
  setTimeout(() => {
    toast.classList.remove('toast-enter');
    toast.classList.add('toast-exit');
    toast.addEventListener('animationend', () => {
      toast.remove();
    });
  }, 4000);
}

// =============================================================================
// COMMS LOG PANEL
// =============================================================================

function appendToCommsLog(notif: CommsNotification): void {
  const entries = document.getElementById('comms-log-entries');
  if (!entries) return;

  const entry = document.createElement('div');
  entry.className = `comms-entry${notif.type === 'gcppa' ? ' gcppa' : ''}`;

  const senderSpan = document.createElement('span');
  senderSpan.className = 'comms-entry-sender';
  senderSpan.style.color = notif.color;
  senderSpan.textContent = notif.sender;

  const msgSpan = document.createElement('span');
  msgSpan.className = 'comms-entry-message';
  msgSpan.textContent = notif.message;

  entry.appendChild(senderSpan);
  entry.appendChild(msgSpan);
  entries.appendChild(entry);
  entries.scrollTop = entries.scrollHeight;
}

// =============================================================================
// PUBLIC API
// =============================================================================

export function pushNotification(notif: CommsNotification): void {
  commsHistory.push(notif);
  appendToCommsLog(notif);
  if (toastsEnabled) {
    showToast(notif);
  }
}

export function toggleCommsLog(): void {
  commsLogOpen = !commsLogOpen;
  const panel = document.getElementById('comms-log-panel');
  if (panel) {
    panel.classList.toggle('hidden', !commsLogOpen);
  }
}

export function isCommsLogOpen(): boolean {
  return commsLogOpen;
}

export function setToastsEnabled(enabled: boolean): void {
  toastsEnabled = enabled;
  const btn = document.getElementById('setting-toasts');
  if (btn) {
    btn.textContent = enabled ? 'ENABLED' : 'DISABLED';
    btn.classList.toggle('off', !enabled);
  }
}

export function areToastsEnabled(): boolean {
  return toastsEnabled;
}

export function clearCommsLog(): void {
  commsHistory.length = 0;
  const entries = document.getElementById('comms-log-entries');
  if (entries) entries.innerHTML = '';
  commsLogOpen = false;
  const panel = document.getElementById('comms-log-panel');
  if (panel) panel.classList.add('hidden');
}
